import { useEffect, useState } from "react";
import { ExternalLink, MapPin, ShieldAlert } from "lucide-react";
import { useReducedMotion } from "./Reveal";
import { useT } from "@/content/i18n";
const COLORS = ["#4ADE80", "#FCD34D", "#F87171"];
function MapCanvas() {
  return <svg viewBox="0 0 300 300" className="absolute inset-0 h-full w-full" preserveAspectRatio="none" aria-hidden>
      <rect width="300" height="300" fill="#EEF2F6" />
      {/* park */}
      <rect x="24" y="30" width="72" height="58" rx="8" fill="#DDEBDD" />
      <rect x="196" y="196" width="82" height="70" rx="8" fill="#DDEBDD" />
      {/* water */}
      <path d="M0 232 C60 216 96 258 150 246 C206 234 246 276 300 262 L300 300 L0 300 Z" fill="#D8E7F2" />
      {/* blocks */}
      {[0, 1, 2, 3, 4].map(r => [0, 1, 2, 3, 4].map(c => <rect key={`${r}-${c}`} x={12 + c * 58} y={12 + r * 58} width="46" height="44" rx="4" fill="#F7F9FB" />))}
      {/* streets */}
      {[0, 1, 2, 3, 4, 5].map(i => <g key={i} stroke="#FFFFFF" strokeWidth="7" strokeLinecap="round">
          <line x1={64 + i * 58 - 58} y1="0" x2={64 + i * 58 - 58} y2="300" />
          <line x1="0" y1={64 + i * 58 - 58} x2="300" y2={64 + i * 58 - 58} />
        </g>)}
      {/* main road */}
      <line x1="0" y1="150" x2="300" y2="150" stroke="#FDE9C8" strokeWidth="11" />
      <line x1="150" y1="0" x2="150" y2="300" stroke="#FDE9C8" strokeWidth="11" />
    </svg>;
}
export function HeatmapVisual() {
  const t = useT();
  const reduced = useReducedMotion();
  const cells = 36;
  const [visible, setVisible] = useState(reduced ? cells : 0);
  useEffect(() => {
    if (reduced) {
      setVisible(cells);
      return;
    }
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      setVisible(n);
      if (n >= cells) clearInterval(id);
    }, 40);
    return () => clearInterval(id);
  }, [reduced]);
  return <div>
      <p className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">
        {t.heatmap.title}
      </p>

      <div className="relative mt-4 aspect-square overflow-hidden rounded-2xl border border-[#E8ECF1]">
        <MapCanvas />
        <div className="absolute inset-0 grid grid-cols-6 grid-rows-6 gap-0 p-2" aria-hidden>
          {Array.from({
          length: cells
        }, (_, i) => {
          const rank = i * 5 % 11 + 1;
          const state = rank <= 3 ? 0 : rank <= 7 ? 1 : 2;
          const shown = i < visible;
          const jitterX = i * 7 % 5 - 2;
          const jitterY = i * 11 % 5 - 2;
          return <div key={i} className="flex items-center justify-center">
                <span className="flex h-[62%] min-h-6 items-center justify-center rounded-full text-[11px] font-semibold text-[#0F172A] shadow-sm ring-2 ring-white/70 transition-all duration-500 motion-reduce:transition-none" style={{
              aspectRatio: "1 / 1",
              backgroundColor: COLORS[state],
              opacity: shown ? 0.94 : 0,
              transform: shown ? `translate(${jitterX}px, ${jitterY}px) scale(1)` : `translate(${jitterX}px, ${jitterY}px) scale(0.6)`
            }}>
                  {rank}
                </span>
              </div>;
        })}
        </div>
        {/* business location */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="flex items-center gap-1 rounded-full bg-[#0F172A] px-2.5 py-1 text-[11px] font-semibold text-white shadow-md">
            <MapPin className="h-3 w-3" aria-hidden />
            {t.heatmap.you}
          </span>
        </div>
      </div>

      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#64748B]">
        {t.heatmap.legend.map((label, i) => <li key={label} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{
          backgroundColor: COLORS[i]
        }} aria-hidden />
            {label}
          </li>)}
      </ul>

      <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl bg-[#FAFBFC] p-3">
          <dt className="text-xs text-[#94A3B8]">{t.heatmap.avg}</dt>
          <dd className="mt-1 font-semibold text-[#0F172A]">6,4</dd>
        </div>
        <div className="rounded-xl bg-[#FAFBFC] p-3">
          <dt className="text-xs text-[#94A3B8]">{t.heatmap.organic}</dt>
          <dd className="mt-1 font-semibold text-[#0F172A]">14</dd>
        </div>
      </dl>
    </div>;
}
function ToneBadge({
  tone
}) {
  const good = tone === "empfehlend";
  return <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{
    backgroundColor: good ? "rgba(107,213,78,0.16)" : "rgba(90,107,122,0.12)",
    color: good ? "#15803D" : "#64748B"
  }}>
      {tone}
    </span>;
}
export function AiVisual() {
  return <div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">
          KI-Sichtbarkeitstest
        </p>
        <span className="rounded-full bg-[#0F172A] px-3 py-1 text-[11px] font-semibold text-white">
          in 3 von 8 Anfragen genannt
        </span>
      </div>
      <div className="mt-4 space-y-3">
        {[{
        model: "ChatGPT",
        text: "Für Elberfeld werden häufig Salon Nordlicht, Friseur Müller und Haarwerk genannt.",
        tone: "empfehlend"
      }, {
        model: "Claude",
        text: "In Wuppertal-Elberfeld gibt es mehrere Salons, unter anderem Friseur Müller.",
        tone: "neutral"
      }].map(b => <div key={b.model} className="rounded-2xl border border-black/5 bg-[#FAFBFC] p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-[#0F172A]">{b.model}</span>
              <ToneBadge tone={b.tone} />
            </div>
            <p className="mt-2 text-sm leading-relaxed text-[#64748B]">{b.text}</p>
          </div>)}
      </div>
      <p className="mt-4 text-xs text-[#94A3B8]">
        Gestellte Frage: „Welchen Friseur in Wuppertal-Elberfeld empfiehlst du?“
      </p>
    </div>;
}
export function LegalVisual() {
  return <div>
      <div className="rounded-2xl border p-4" style={{
      borderColor: "rgba(228,87,76,0.35)",
      backgroundColor: "rgba(228,87,76,0.06)"
    }}>
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} aria-hidden style={{
          color: "#EF4444"
        }} />
          <div>
            <p className="text-sm font-semibold text-[#0F172A]">
              Möglicherweise lizenzpflichtige Stockfotos
            </p>
            <p className="mt-1 text-sm leading-relaxed text-[#64748B]">
              Ein Bild auf Ihrer Startseite wurde per Rückwärts-Bildsuche auf 8 weiteren
              unabhängigen Websites gefunden. Das deutet auf ein Agenturbild hin.
            </p>
            <ul className="mt-3 space-y-1.5">
              {["Fundstelle 1 · beispiel-agentur.de", "Fundstelle 2 · beispiel-portal.de"].map(l => <li key={l}>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#3B82F6]">
                    <ExternalLink size={13} aria-hidden /> {l}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
      <ul className="mt-4 grid gap-2 text-sm text-[#64748B]">
        {["Impressum vorhanden", "Datenschutzerklärung vorhanden", "Google Fonts von Google-Servern geladen", "Tracking startet vor der Einwilligung"].map((row, i) => <li key={row} className="flex items-center gap-2 rounded-xl bg-[#FAFBFC] px-3 py-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{
          backgroundColor: i < 2 ? "#4ADE80" : "#F87171"
        }} aria-hidden />
            {row}
          </li>)}
      </ul>
      <p className="mt-4 text-xs font-medium text-[#94A3B8]">Dies ist keine Rechtsberatung.</p>
    </div>;
}
export function ReputationVisual() {
  const rows = [{
    name: "Friseur Müller",
    rating: "4,3",
    count: "38",
    last: "vor 41 Tagen",
    theme: "Wartezeit",
    own: true
  }, {
    name: "Salon Nordlicht",
    rating: "4,8",
    count: "212",
    last: "vor 3 Tagen",
    theme: "Parkplatz"
  }, {
    name: "Haarwerk Elberfeld",
    rating: "4,6",
    count: "97",
    last: "vor 9 Tagen",
    theme: "Preis"
  }];
  return <div className="overflow-x-auto">
      <p className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">
        Vergleich mit den stärksten Mitbewerbern
      </p>
      <table className="mt-4 w-full min-w-[28rem] border-collapse text-sm">
        <thead>
          <tr className="text-left text-xs text-[#94A3B8]">
            <th scope="col" className="pb-2 font-medium">Unternehmen</th>
            <th scope="col" className="pb-2 font-medium">Rating</th>
            <th scope="col" className="pb-2 font-medium">Anzahl</th>
            <th scope="col" className="pb-2 font-medium">Letzte</th>
            <th scope="col" className="pb-2 font-medium">Kritikthema</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => <tr key={r.name} className="align-middle" style={r.own ? {
          backgroundColor: "rgba(45,125,255,0.07)"
        } : undefined}>
              <td className="rounded-l-lg px-2 py-3 font-semibold text-[#0F172A]">
                {r.name}
                {r.own && <span className="ml-2 rounded-full bg-[#0F172A] px-2 py-0.5 text-[10px] font-semibold text-white">
                    Sie
                  </span>}
              </td>
              <td className="px-2 py-3 text-[#64748B]">{r.rating}</td>
              <td className="px-2 py-3 text-[#64748B]">{r.count}</td>
              <td className="px-2 py-3 text-[#64748B]">{r.last}</td>
              <td className="rounded-r-lg px-2 py-3 text-[#64748B]">{r.theme}</td>
            </tr>)}
        </tbody>
      </table>
      <p className="mt-4 text-xs text-[#94A3B8]">
        Bis zu 50 Bewertungen nach Datum · Antwortquote 21 % · Antworten wirken überwiegend wie Vorlagen
      </p>
    </div>;
}
export function PlanVisual() {
  const months = [{
    m: "Monat 1",
    items: ["Unternehmensprofil vervollständigen", "Fehlende Pflichtangaben ergänzen"]
  }, {
    m: "Monat 2",
    items: ["Zwei Stadtteil-Seiten anlegen", "Bewertungen aktiv einholen"]
  }, {
    m: "Monat 3",
    items: ["FAQ mit echten Kundenfragen", "Bildquellen ersetzen oder belegen"]
  }];
  return <div className="grid gap-3">
      {months.map((mo, i) => <div key={mo.m} className="rounded-2xl border border-black/5 bg-[#FAFBFC] p-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white" style={{
          backgroundImage: "linear-gradient(135deg,#3B82F6,#4ADE80)"
        }}>
              {i + 1}
            </span>
            <p className="text-sm font-semibold text-[#0F172A]">{mo.m}</p>
          </div>
          <ul className="mt-3 space-y-1.5 text-sm text-[#64748B]">
            {mo.items.map(it => <li key={it} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#3B82F6]" aria-hidden />
                {it}
              </li>)}
          </ul>
        </div>)}
    </div>;
}
export const stepVisuals = [HeatmapVisual, AiVisual, LegalVisual, ReputationVisual, PlanVisual];